import React from 'react';

const LoadingSpinner = ({ 
  size = 'large', 
  message = 'Loading amazing movies...', 
  type = 'movies' 
}) => {
  const sizeClasses = {
    small: 'w-8 h-8',
    medium: 'w-12 h-12',
    large: 'w-16 h-16',
    xlarge: 'w-20 h-20'
  };

  const messages = {
    movies: [
      'Loading amazing movies...',
      'Fetching cinematic masterpieces...',
      'Preparing your movie experience...',
      'Discovering great films...',
      'Loading movie magic...'
    ],
    favorites: [
      'Loading your favorites...',
      'Gathering your loved movies...',
      'Preparing your collection...'
    ],
    collections: [
      'Loading your collections...',
      'Organizing your movie lists...',
      'Preparing your curated content...'
    ],
    details: [
      'Loading movie details...',
      'Fetching movie information...',
      'Preparing the details...'
    ]
  };

  const getRandomMessage = () => {
    const messageArray = messages[type] || messages.movies;
    return messageArray[Math.floor(Math.random() * messageArray.length)];
  };

  return (
    <div className="flex flex-col items-center justify-center py-20 px-4">
      
      {/* Main Spinner */}
      <div className="relative mb-8">
        {/* Outer Ring */}
        <div className={`${sizeClasses[size]} border-4 border-blue-200 dark:border-blue-800 rounded-full animate-spin`}>
          <div className="absolute top-0 left-0 w-full h-full border-4 border-transparent border-t-blue-600 rounded-full animate-spin"></div>
        </div>
        
        {/* Inner Icon */}
        <div className="absolute inset-0 flex items-center justify-center">
          <svg 
            className={`${size === 'small' ? 'w-4 h-4' : size === 'medium' ? 'w-6 h-6' : 'w-8 h-8'} text-blue-600 animate-pulse`} 
            fill="currentColor" 
            viewBox="0 0 24 24"
          >
            <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z"/>
          </svg>
        </div>

        {/* Pulsing Background */}
        <div className={`absolute inset-0 ${sizeClasses[size]} bg-blue-500 rounded-full animate-ping opacity-10`}></div>
      </div>

      {/* Loading Message */}
      <div className="text-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2 animate-pulse">
          {message || getRandomMessage()}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 animate-pulse">
          Please wait while we fetch the best content for you
        </p>
      </div>

      {/* Progress Dots */}
      <div className="flex space-x-2 mb-8">
        <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
        <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
        <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
      </div>

      {/* Movie Strip Animation */}
      <div className="relative w-64 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-500 to-transparent h-full animate-slide-right"></div>
      </div>

      {/* Fun Movie Facts */}
      <div className="mt-8 text-center max-w-md">
        <p className="text-sm text-gray-600 dark:text-gray-400 italic">
          💡 Did you know? The first movie ever made was only 2.11 seconds long!
        </p>
      </div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes slide-right {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        
        .animate-slide-right {
          animation: slide-right 1.5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

// Skeleton Loading Component for Movie Cards
export const MovieCardSkeleton = () => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-md border border-gray-100 dark:border-gray-700 animate-pulse">
      {/* Image Skeleton */}
      <div className="aspect-[2/3] bg-gray-200 dark:bg-gray-700"></div>
      
      {/* Content Skeleton */}
      <div className="p-4 space-y-3">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
        <div className="flex items-center justify-between">
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
        </div>
      </div>
    </div>
  );
};

// Grid Skeleton for Multiple Cards
export const MoviesGridSkeleton = ({ count = 12 }) => {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
      {Array.from({ length: count }).map((_, index) => (
        <MovieCardSkeleton key={index} />
      ))}
    </div>
  );
};

// Page Loading Component
export const PageLoading = ({ title = "Loading Page..." }) => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <div className="text-center">
        {/* Large Spinner */}
        <div className="relative mb-8">
          <div className="w-20 h-20 border-4 border-blue-200 dark:border-blue-800 rounded-full animate-spin">
            <div className="absolute top-0 left-0 w-full h-full border-4 border-transparent border-t-blue-600 rounded-full animate-spin"></div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <svg className="w-8 h-8 text-blue-600 animate-pulse" fill="currentColor" viewBox="0 0 24 24">
              <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z"/>
            </svg>
          </div>
        </div>

        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{title}</h1>
        <div className="flex justify-center space-x-2">
          <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
          <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
          <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
        </div>
      </div>
    </div>
  );
};

// Button Loading State
export const ButtonLoading = ({ children, loading = false, ...props }) => {
  return (
    <button {...props} disabled={loading} className={`${props.className} relative`}>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      <span className={loading ? 'opacity-0' : 'opacity-100'}>
        {children}
      </span>
    </button>
  );
};

// Inline Loading for Small Components
export const InlineLoading = ({ message = "Loading..." }) => {
  return (
    <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      <span className="text-sm">{message}</span>
    </div>
  );
};

// Search Loading
export const SearchLoading = () => {
  return (
    <div className="flex items-center justify-center py-8">
      <div className="flex items-center space-x-3">
        <div className="relative">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <svg className="absolute inset-0 m-auto w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
        </div>
        <span className="text-gray-600 dark:text-gray-400 font-medium">Searching movies...</span>
      </div>
    </div>
  );
};

// Image Loading Placeholder
export const ImageLoading = ({ className = "", alt = "Loading..." }) => {
  return (
    <div className={`bg-gray-200 dark:bg-gray-700 animate-pulse flex items-center justify-center ${className}`}>
      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
      </svg>
    </div>
  );
};

// Content Loading with Multiple Lines
export const ContentLoading = ({ lines = 3 }) => {
  return (
    <div className="space-y-3 animate-pulse">
      {Array.from({ length: lines }).map((_, index) => (
        <div
          key={index}
          className={`h-4 bg-gray-200 dark:bg-gray-700 rounded ${
            index === lines - 1 ? 'w-2/3' : 'w-full'
          }`}
        ></div>
      ))}
    </div>
  );
};

// Circular Progress
export const CircularProgress = ({ progress = 0, size = 60, strokeWidth = 6 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className="relative">
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          className="text-gray-200 dark:text-gray-700"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="text-blue-600 transition-all duration-300"
        />
      </svg>
      {/* Progress text */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-semibold text-gray-900 dark:text-white">
          {progress}%
        </span>
      </div>
    </div>
  );
};

// Pulsing Loader
export const PulseLoader = ({ count = 3, size = 'medium' }) => {
  const sizeClasses = {
    small: 'w-2 h-2',
    medium: 'w-3 h-3',
    large: 'w-4 h-4'
  };

  return (
    <div className="flex space-x-2">
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className={`${sizeClasses[size]} bg-blue-600 rounded-full animate-pulse`}
          style={{
            animationDelay: `${index * 0.3}s`,
            animationDuration: '1.2s'
          }}
        ></div>
      ))}
    </div>
  );
};

// Skeleton Text
export const SkeletonText = ({ 
  lines = 1, 
  className = "", 
  animate = true,
  widths = ['w-full', 'w-5/6', 'w-4/6'] 
}) => {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, index) => (
        <div
          key={index}
          className={`h-4 bg-gray-200 dark:bg-gray-700 rounded ${
            widths[index % widths.length]
          } ${animate ? 'animate-pulse' : ''}`}
        ></div>
      ))}
    </div>
  );
};

export default LoadingSpinner;